import { isAxiosError } from "axios"
import { vitalFill } from "@/helpers/vitals"
import {
  IBattleAbilityButton,
  IBattleActionChoice,
  IBattleActionsModel,
  IBattleEndStep,
  IBattleFailureEffects,
  IBattleFieldCellView,
  IBattlePotionButton,
  IBattleRoundPick,
  IBattleSide,
  IBattleTextPart,
  IBattleZone,
  IBattleScreenStatus,
  IRoundLogLine
} from "@/hooks/useBattleScreen/types"
import {
  ABILITY_CAST_OUTCOME,
  ABILITY_EFFECT_KIND,
  BATTLE_ACTION_TYPE,
  BATTLE_ATTACK_FORM,
  BATTLE_CELL_KIND,
  BATTLE_FIGHTER_KIND,
  BATTLE_ROUND_RESOLVER,
  BATTLE_STATE,
  BATTLE_TEAM,
  COMBAT_EVENT_TYPE,
  IBattleActionRequest,
  IBattleCell,
  IBattleFighter,
  IBattleLegalActions,
  IBattleRound,
  IBattleView,
  ICombatEvent,
  MOVE_STATUS
} from "@/store/apis/battleAPI/battle.types"
import { planBattleEnd } from "@/store/apis/battleAPI/battleEnd"
import { IBattleFailure } from "@/store/apis/battleAPI/battleFailure"
import { IVitalView } from "@/types/vitals.types"

const line = (
  key: string,
  params: Record<string, number> = {},
  abilityId: string | null = null,
  itemCode: string | null = null
): IRoundLogLine => ({ key, params, abilityId, itemCode })

const failedCastKeys: Record<ABILITY_CAST_OUTCOME, string> = {
  [ABILITY_CAST_OUTCOME.SUCCESS]: "log.youCast",
  [ABILITY_CAST_OUTCOME.NO_ENERGY]: "log.castNoEnergy",
  [ABILITY_CAST_OUTCOME.ON_COOLDOWN]: "log.castOnCooldown",
  [ABILITY_CAST_OUTCOME.NO_VALID_TARGET]: "log.castNoTarget",
  [ABILITY_CAST_OUTCOME.TARGET_ELIMINATED]: "log.castTargetFell"
}

const failedMoveKeys: Record<
  Exclude<MOVE_STATUS, MOVE_STATUS.SUCCESS>,
  string
> = {
  [MOVE_STATUS.CELL_OCCUPIED]: "log.moveBlocked",
  [MOVE_STATUS.COLLISION_LOST]: "log.moveCollision",
  [MOVE_STATUS.PATH_BLOCKED]: "log.movePathBlocked"
}

const linesForEvent = (
  event: ICombatEvent,
  heroFighterId: string
): IRoundLogLine[] => {
  const isHero = (id: string) => id === heroFighterId
  const pick = (id: string, you: string, mob: string) =>
    isHero(id) ? you : mob

  switch (event.type) {
    case COMBAT_EVENT_TYPE.STRIKE: {
      const { hit, fizzled, crit, damage } = event.result
      const attacker = event.attackerId
      if (event.onEliminatedTarget) {
        return [
          line(pick(attacker, "log.youStrikeFallen", "log.mobStrikeFallen"))
        ]
      }
      if (!hit || fizzled) {
        return [line(pick(attacker, "log.youMiss", "log.mobMiss"))]
      }
      return [
        line(
          crit
            ? pick(attacker, "log.youCrit", "log.mobCrit")
            : pick(attacker, "log.youHit", "log.mobHit"),
          { damage }
        )
      ]
    }
    case COMBAT_EVENT_TYPE.ABILITY_CAST: {
      const key =
        event.outcome === ABILITY_CAST_OUTCOME.SUCCESS
          ? pick(event.casterId, "log.youCast", "log.mobCast")
          : failedCastKeys[event.outcome]
      return [line(key, {}, event.abilityId)]
    }
    case COMBAT_EVENT_TYPE.MOVE: {
      if (event.status === MOVE_STATUS.SUCCESS) {
        return [
          isHero(event.characterId)
            ? line("log.youMove", { energy: event.energySpent })
            : line("log.mobMove")
        ]
      }
      return isHero(event.characterId)
        ? [line(failedMoveKeys[event.status])]
        : []
    }
    case COMBAT_EVENT_TYPE.ABILITY_HIT: {
      const key =
        event.kind === ABILITY_EFFECT_KIND.HEAL
          ? pick(event.targetId, "log.abilityHealYou", "log.abilityHealMob")
          : pick(event.targetId, "log.abilityHitYou", "log.abilityHitMob")
      return [line(key, { amount: event.amount }, event.abilityId)]
    }
    case COMBAT_EVENT_TYPE.EFFECT_TICK: {
      const lines: IRoundLogLine[] = []
      if (event.damage > 0) {
        lines.push(
          line(
            pick(event.characterId, "log.tickDamageYou", "log.tickDamageMob"),
            { damage: event.damage }
          )
        )
      }
      if (event.heal > 0) {
        lines.push(
          line(pick(event.characterId, "log.tickHealYou", "log.tickHealMob"), {
            heal: event.heal
          })
        )
      }
      return lines
    }
    case COMBAT_EVENT_TYPE.ABILITY_EFFECT_APPLIED:
      return [
        line(
          pick(event.targetId, "log.effectOnYou", "log.effectOnMob"),
          {},
          event.abilityId
        )
      ]
    case COMBAT_EVENT_TYPE.CONSUMABLE_USED: {
      if (!isHero(event.characterId)) {
        return []
      }
      const potionLine = (key: string, params: Record<string, number> = {}) =>
        line(key, params, null, event.consumableCode)
      const lines = [
        ...(event.hpRestored > 0
          ? [potionLine("log.potionHp", { hp: event.hpRestored })]
          : []),
        ...(event.manaRestored > 0
          ? [potionLine("log.potionMana", { mana: event.manaRestored })]
          : [])
      ]
      return lines.length > 0 ? lines : [potionLine("log.potionNoEffect")]
    }
    case COMBAT_EVENT_TYPE.ELIMINATION:
      return [line(pick(event.characterId, "log.youFell", "log.mobFell"))]
    default:
      return []
  }
}

export const formatRoundLog = (
  round: IBattleRound | null,
  heroFighterId: string
): IRoundLogLine[] => {
  if (!round) {
    return []
  }
  const timeout =
    round.resolvedBy === BATTLE_ROUND_RESOLVER.TIMEOUT
      ? [line("log.timeout")]
      : []
  return [
    ...timeout,
    ...round.events.flatMap((event) => linesForEvent(event, heroFighterId))
  ]
}

export const heroOf = (view: IBattleView): IBattleFighter | undefined =>
  view.fighters.find((fighter) => fighter.fighterId === view.heroFighterId)

export const mobOf = (view: IBattleView): IBattleFighter | undefined =>
  view.fighters.find((fighter) => fighter.kind === BATTLE_FIGHTER_KIND.MOB)

const abilityDetails = (
  energyCost: number,
  cooldownRounds: number,
  cooldownLeft: number | null
): IBattleTextPart[] => {
  if (cooldownLeft !== null) {
    return [{ key: "screen.cooldown", params: { rounds: cooldownLeft } }]
  }
  return [
    ...(energyCost > 0
      ? [{ key: "screen.energyCost", params: { cost: energyCost } }]
      : []),
    ...(cooldownRounds > 0
      ? [{ key: "screen.cooldownBase", params: { rounds: cooldownRounds } }]
      : [])
  ]
}

export const emptyRoundPick = (round: number): IBattleRoundPick => ({
  round,
  form: BATTLE_ATTACK_FORM.ARROWS,
  consumableCode: null
})

export const roundPickFor = (
  stored: IBattleRoundPick | null,
  round: number
): IBattleRoundPick =>
  stored !== null && stored.round === round ? stored : emptyRoundPick(round)

export const staffChosen = (
  pick: IBattleRoundPick,
  legal: IBattleLegalActions
): boolean =>
  pick.form === BATTLE_ATTACK_FORM.STAFF && legal.staffTargetIds.length > 0

const attackFormOf = (
  pick: IBattleRoundPick,
  legal: IBattleLegalActions
): Pick<IBattleActionRequest, "attackForm"> =>
  staffChosen(pick, legal) ? { attackForm: BATTLE_ATTACK_FORM.STAFF } : {}

export const togglePotion = (
  pick: IBattleRoundPick,
  code: string
): IBattleRoundPick => ({
  ...pick,
  consumableCode: pick.consumableCode === code ? null : code
})

const consumableOf = (
  pick: IBattleRoundPick,
  legal: IBattleLegalActions
): Pick<IBattleActionRequest, "consumableCode"> =>
  pick.consumableCode !== null &&
  legal.consumableCodes.includes(pick.consumableCode)
    ? { consumableCode: pick.consumableCode }
    : {}

export const sendAllowed = (
  inFlightRound: number | null,
  round: number
): boolean => inFlightRound !== round

interface ICanHeroActInput {
  view: IBattleView
  legal: IBattleLegalActions | undefined
  busy: boolean
}

interface IDeriveBattleActionsInput extends ICanHeroActInput {
  pick: IBattleRoundPick
}

export const canHeroAct = ({ view, legal, busy }: ICanHeroActInput): boolean =>
  view.state === BATTLE_STATE.OPEN && !view.auto && !busy && legal !== undefined

export const deriveBattleActions = ({
  view,
  legal,
  busy,
  pick
}: IDeriveBattleActionsInput): IBattleActionsModel => {
  const isOpen = view.state === BATTLE_STATE.OPEN
  const canAct = canHeroAct({ view, legal, busy })
  const mobId = mobOf(view)?.fighterId
  const hero = heroOf(view)
  const cooldowns = hero?.cooldowns ?? {}

  const abilities: IBattleAbilityButton[] = (hero?.abilities ?? []).map(
    ({ id, energyCost, cooldownRounds }) => {
      const left = cooldowns[id]
      const cooldownLeft = left > 0 ? left : null
      const isLegal =
        legal !== undefined &&
        (legal.attackAbilityIds.includes(id) ||
          legal.standaloneAbilityIds.includes(id))
      return {
        abilityId: id,
        enabled: canAct && cooldownLeft === null && isLegal,
        energyCost,
        cooldownRounds,
        cooldownLeft,
        details: abilityDetails(energyCost, cooldownRounds, cooldownLeft)
      }
    }
  )

  const potions: IBattlePotionButton[] = (legal?.consumableCodes ?? []).flatMap(
    (code) => {
      const quantity =
        hero?.consumables.find((stock) => stock.code === code)?.quantity ?? 0
      return quantity > 0
        ? [
            {
              code,
              quantity,
              selected: pick.consumableCode === code,
              enabled: canAct
            }
          ]
        : []
    }
  )

  const attackTargets =
    legal === undefined
      ? []
      : staffChosen(pick, legal)
        ? legal.staffTargetIds
        : legal.attackTargetIds

  return {
    attack: {
      enabled: canAct && mobId !== undefined && attackTargets.includes(mobId)
    },
    attackForm:
      legal === undefined || legal.staffTargetIds.length === 0
        ? null
        : { selected: pick.form, enabled: canAct },
    abilities,
    potions,
    autoOn: view.auto,
    autoEnabled: isOpen && !busy,
    fleeEnabled: isOpen && !busy
  }
}

export const abilityTargetFor = (
  abilityId: string,
  view: IBattleView,
  legal: IBattleLegalActions
): string | undefined => {
  const targetIds =
    legal.standaloneAbilityTargets.find(
      (entry) => entry.abilityId === abilityId
    )?.targetIds ?? []
  const mobId = mobOf(view)?.fighterId
  return (
    [mobId, view.heroFighterId].find(
      (id): id is string => id !== undefined && targetIds.includes(id)
    ) ?? targetIds[0]
  )
}

const mainActionFor = (
  choice: IBattleActionChoice,
  view: IBattleView,
  legal: IBattleLegalActions,
  pick: IBattleRoundPick
): IBattleActionRequest | null => {
  if (choice.kind === "move") {
    return { type: BATTLE_ACTION_TYPE.MOVE, target: choice.target }
  }
  const mobId = mobOf(view)?.fighterId
  if (choice.kind === "attack") {
    return mobId === undefined
      ? null
      : {
          type: BATTLE_ACTION_TYPE.ATTACK,
          targetId: mobId,
          ...attackFormOf(pick, legal)
        }
  }
  if (legal.attackAbilityIds.includes(choice.abilityId)) {
    return mobId === undefined
      ? null
      : {
          type: BATTLE_ACTION_TYPE.ATTACK,
          targetId: mobId,
          abilityId: choice.abilityId,
          ...attackFormOf(pick, legal)
        }
  }
  if (legal.standaloneAbilityIds.includes(choice.abilityId)) {
    const abilityTargetId = abilityTargetFor(choice.abilityId, view, legal)
    return {
      type: BATTLE_ACTION_TYPE.ABILITY_ONLY,
      abilityId: choice.abilityId,
      ...(abilityTargetId !== undefined && { abilityTargetId })
    }
  }
  return null
}

export const actionRequestFor = (
  choice: IBattleActionChoice,
  view: IBattleView,
  legal: IBattleLegalActions,
  pick: IBattleRoundPick
): IBattleActionRequest | null => {
  const action = mainActionFor(choice, view, legal, pick)
  return action && { ...action, ...consumableOf(pick, legal) }
}

const FAILURE_EFFECTS: Record<IBattleFailure, IBattleFailureEffects> = {
  CONSUMABLE_GONE: {
    clearPotion: true,
    rereadBattle: true,
    rereadLegal: true,
    toastKey: "battle:toast.potionGone"
  },
  REREAD: {
    clearPotion: false,
    rereadBattle: true,
    rereadLegal: false,
    toastKey: null
  },
  REJECTED: {
    clearPotion: false,
    rereadBattle: false,
    rereadLegal: false,
    toastKey: "battle:toast.notPossible"
  },
  FAILED: {
    clearPotion: false,
    rereadBattle: false,
    rereadLegal: false,
    toastKey: "battle:toast.failed"
  }
}

export const battleFailureEffects = (
  failure: IBattleFailure
): IBattleFailureEffects => FAILURE_EFFECTS[failure]

export const countdownSeconds = (
  deadline: string | null,
  nowMs: number
): number => {
  if (deadline === null) {
    return 0
  }
  return Math.max(0, Math.ceil((Date.parse(deadline) - nowMs) / 1000))
}

export const REREAD_AUTO_POLL_MS = 5000
export const REREAD_DEADLINE_GRACE_MS = 2000

/** Delay before the watchdog re-reads an open battle over REST; null when it should not. */
export const rereadDelayMs = (
  view: IBattleView | undefined,
  nowMs: number
): number | null => {
  if (!view || view.state !== BATTLE_STATE.OPEN) {
    return null
  }
  if (view.auto) {
    return REREAD_AUTO_POLL_MS
  }
  if (view.roundDeadline === null) {
    return null
  }
  return (
    Math.max(0, Date.parse(view.roundDeadline) - nowMs) +
    REREAD_DEADLINE_GRACE_MS
  )
}

export const formatCountdown = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60)
  const rest = seconds % 60
  return `${minutes}:${String(rest).padStart(2, "0")}`
}

export const fighterVitals = (
  fighter: IBattleFighter,
  options: { withEnergy: boolean }
): IVitalView[] => {
  const view = (code: string, current: number, max: number): IVitalView => ({
    code,
    current,
    max,
    fill: vitalFill(current, max)
  })
  const vitals = [view("hp", fighter.hp, fighter.maxHp)]
  if (fighter.maxMana !== null) {
    vitals.push(view("mana", fighter.mana ?? 0, fighter.maxMana))
  }
  if (options.withEnergy) {
    vitals.push(view("energy", fighter.energy, fighter.maxEnergy))
  }
  return vitals
}

interface IScreenStatusInput {
  view: IBattleView | undefined
  isError: boolean
  errorStatus: number | null
}

export const screenStatusOf = ({
  view,
  isError,
  errorStatus
}: IScreenStatusInput): IBattleScreenStatus | "gone" => {
  if (view) {
    return "ready"
  }
  if (isError) {
    return errorStatus === 404 ? "gone" : "error"
  }
  return "loading"
}

export const errorStatusOf = (error: unknown): number | null =>
  isAxiosError(error) ? (error.response?.status ?? null) : null

/** A closed battle always closes the screen; only the follow-up window is once per id via `claim`. */
export const battleEndStep = (
  view: IBattleView,
  claim: (battleId: string) => boolean
): IBattleEndStep | null => {
  if (view.state === BATTLE_STATE.OPEN) {
    return null
  }
  const plan = planBattleEnd(view)
  return { followUp: plan && claim(view.id) ? plan : null }
}

export const cellKey = ({ col, row }: IBattleCell): string => `${col}:${row}`

export const deriveMoveTargets = (
  legal: IBattleLegalActions | undefined,
  canAct: boolean
): Record<string, IBattleCell> => {
  if (!canAct || !legal) {
    return {}
  }
  return Object.fromEntries(
    legal.moveTargets.map((cell) => [cellKey(cell), cell])
  )
}

const TEAMS: readonly BATTLE_TEAM[] = [BATTLE_TEAM.A, BATTLE_TEAM.B]

export const deriveBattleField = (
  view: IBattleView,
  moveTargets: Record<string, IBattleCell> = {}
): IBattleFieldCellView[] => {
  const heroTeam = heroOf(view)?.team ?? BATTLE_TEAM.A
  const { zones } = view.field
  const sideOf = (team: BATTLE_TEAM): IBattleSide =>
    team === heroTeam ? "ally" : "enemy"
  const zoneOf = (row: number): IBattleZone => {
    const team = TEAMS.find((candidate) => {
      const { frontRow, backRow } = zones[candidate]
      return (
        row >= Math.min(frontRow, backRow) && row <= Math.max(frontRow, backRow)
      )
    })
    return team === undefined ? "neutral" : sideOf(team)
  }
  const heroZone = zones[heroTeam]
  const rowDirection = heroZone.backRow < heroZone.frontRow ? -1 : 1

  return [...view.grid]
    .sort((a, b) => (a.row - b.row) * rowDirection || a.col - b.col)
    .map(({ col, row, kind, fighterId }) => {
      const cell = { col, row }
      const key = cellKey(cell)
      const blocked = kind === BATTLE_CELL_KIND.IMPASSABLE
      const fighter = view.fighters.find((f) => f.fighterId === fighterId)
      const token =
        fighter && fighter.alive && fighter.cell !== null
          ? {
              fighterId: fighter.fighterId,
              archetype: fighter.archetype,
              isHero: fighter.fighterId === view.heroFighterId,
              side: sideOf(fighter.team)
            }
          : null
      return {
        key,
        cell,
        zone: zoneOf(row),
        blocked,
        token,
        move: blocked ? null : (moveTargets[key] ?? null)
      }
    })
}

export const fighterClassKeys = (
  fighter: IBattleFighter | undefined
): string[] => {
  if (!fighter) {
    return []
  }
  return [
    `archetype.${fighter.archetype}`,
    `weapon.${fighter.weaponType}`,
    ...(fighter.specialization === null
      ? []
      : [`specialization.${fighter.specialization}`])
  ]
}
